import NumberInputComponent from "@/components/inputs/NumberInput";
import useRequest from "@/lib/request/useRequest";
import { Field, LossFunctionParameters } from "@/types/field";
import { yupResolver } from "@hookform/resolvers/yup";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Button,
    Card,
    CardBody,
    Divider,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Spinner,
    Tab,
    Tabs,
} from "@heroui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { InferType, number, object } from "yup";
import { isNumber } from "@/lib/utils/numbers";
import {
    DSSATPayload,
    DSSATSoilConfiguration,
    Layer,
    LAYERS,
} from "@/types/dssatSoilConfiguration";

const DSSAT_LAYER_DEPTHS = ["5", "15", "30", "60", "100", "200"] as const;

const DSSAT_LAYER_COLUMNS = [
    "SBDM",
    "SCEC",
    "SDUL",
    "SLLL",
    "SLOC",
    "Adj SLOC",
    "SRGF",
    "SSAT",
] as const;

const ZERO_SOIL_LAYER = {
    SBDM: 0,
    SCEC: 0,
    SDUL: 0,
    SLLL: 0,
    SLOC: 0,
    SRGF: 0,
    SSAT: 0,
} as const;

const DSSAT_SOIL_SOURCE_LABELS: Record<string, string> = {
    ssurgo: "SSURGO",
    openlandmap: "OpenLandMap",
    soilgrids: "SoilGrids",
    smap: "S-Map",
    slga: "SLGA",
    ansis: "ANSIS",
    mapbiomas: "MapBiomas",
    user_defined: "User Defined",
};

function formatSoilSource(source: string): string {
    return DSSAT_SOIL_SOURCE_LABELS[source] ?? source;
}

function organicMatterToOrganicCarbon(organicMatterPercent: number): number {
    return organicMatterPercent * 0.55;
}

function displaySlocsAfterFieldOrganicMatter(
    layers: LAYERS,
    organicMatterPercent: number,
): Record<(typeof DSSAT_LAYER_DEPTHS)[number], number | null> {
    const sortedDepths = [...DSSAT_LAYER_DEPTHS].sort(
        (a, b) => Number(a) - Number(b),
    );
    const topDepth = sortedDepths[0];
    const topLayerSlocRaw =
        topDepth != null ? layers[topDepth]?.SLOC : undefined;
    const topLayerSloc =
        typeof topLayerSlocRaw === "number" && Number.isFinite(topLayerSlocRaw)
            ? topLayerSlocRaw
            : null;

    const scaleLayerSloc = (sloc: number | null): number | null => {
        if (sloc == null) return null;
        if (
            topLayerSloc == null ||
            topLayerSloc <= 0 ||
            organicMatterPercent == null ||
            !Number.isFinite(organicMatterPercent)
        ) {
            return sloc;
        }
        const targetSurfaceSloc =
            organicMatterToOrganicCarbon(organicMatterPercent);
        if (targetSurfaceSloc <= 0 || targetSurfaceSloc === topLayerSloc) {
            return sloc;
        }
        const scaleFactor = targetSurfaceSloc / topLayerSloc;
        return Math.round(sloc * scaleFactor * 1000) / 1000;
    };

    return Object.fromEntries(
        DSSAT_LAYER_DEPTHS.map((depth) => {
            const raw = layers[depth]?.SLOC;
            const sloc = isNumber(raw) ? Number(raw) : null;
            return [depth, scaleLayerSloc(sloc)];
        }),
    ) as Record<(typeof DSSAT_LAYER_DEPTHS)[number], number | null>;
}

const soilLayerSchema = object({
    SBDM: number().typeError("Required").required("Required"),
    SCEC: number().typeError("Required").required("Required"),
    SDUL: number().typeError("Required").required("Required"),
    SLLL: number().typeError("Required").required("Required"),
    SLOC: number().typeError("Required").required("Required"),
    SRGF: number().typeError("Required").required("Required"),
    SSAT: number().typeError("Required").required("Required"),
});

const dssatSoilFormSchema = object({
    ALBEDO: number().typeError("Required").required("Required"),
    LAYERS: object({
        "5": soilLayerSchema,
        "15": soilLayerSchema,
        "30": soilLayerSchema,
        "60": soilLayerSchema,
        "100": soilLayerSchema,
        "200": soilLayerSchema,
    }).required(),
    DRAINAGE_RATE: number().typeError("Required").required("Required"),
    RUNOFF_CURVE_NO: number().typeError("Required").required("Required"),
    FERTILITY_FACTOR: number().typeError("Required").required("Required"),
    S1_EVAPORATION_LIMIT: number().typeError("Required").required("Required"),
    MINERALIZATION_FACTOR: number().typeError("Required").required("Required"),
});

const lossFunctionFormSchema = object({
    w1: number().typeError("Required").required("Required"),
    w2: number().typeError("Required").required("Required"),
    w3: number().typeError("Required").required("Required"),
    w4: number().typeError("Required").required("Required"),
    w5: number().typeError("Required").required("Required"),
    n_cost_per_lb: number().typeError("Required").required("Required"),
    app_cost: number().typeError("Required").required("Required"),
    crop_price: number().typeError("Required").required("Required"),
    bu_per_n: number().typeError("Required").required("Required"),
    niad_thresh: number().typeError("Required").required("Required"),
    remob_thresh: number().typeError("Required").required("Required"),
    roi_safe_frac: number().typeError("Required").required("Required"),
    roi_gap_power: number().typeError("Required").required("Required"),
});

const schema = object({
    dssat_soil: dssatSoilFormSchema,
    loss_function: lossFunctionFormSchema,
});

type ParameterFormValues = InferType<typeof schema>;

function emptySoilForm(): DSSATPayload {
    return {
        ALBEDO: 0,
        DRAINAGE_RATE: 0,
        RUNOFF_CURVE_NO: 0,
        FERTILITY_FACTOR: 0,
        S1_EVAPORATION_LIMIT: 0,
        MINERALIZATION_FACTOR: 0,
        LAYERS: {
            "5": { ...ZERO_SOIL_LAYER },
            "15": { ...ZERO_SOIL_LAYER },
            "30": { ...ZERO_SOIL_LAYER },
            "60": { ...ZERO_SOIL_LAYER },
            "100": { ...ZERO_SOIL_LAYER },
            "200": { ...ZERO_SOIL_LAYER },
        },
    };
}

function configurationToSoilForm(config: DSSATSoilConfiguration): DSSATPayload {
    const payload = config.dssat_soil;
    return {
        ALBEDO: payload?.ALBEDO ?? config.albedo,
        DRAINAGE_RATE: payload?.DRAINAGE_RATE ?? config.drainage_rate,
        RUNOFF_CURVE_NO: payload?.RUNOFF_CURVE_NO ?? config.runoff_curve_no,
        FERTILITY_FACTOR: payload?.FERTILITY_FACTOR ?? config.fertility_factor,
        S1_EVAPORATION_LIMIT:
            payload?.S1_EVAPORATION_LIMIT ?? config.s1_evaporation_limit,
        MINERALIZATION_FACTOR:
            payload?.MINERALIZATION_FACTOR ?? config.mineralization_factor,
        LAYERS: {
            "5": { ...ZERO_SOIL_LAYER, ...payload?.LAYERS?.["5"] },
            "15": { ...ZERO_SOIL_LAYER, ...payload?.LAYERS?.["15"] },
            "30": { ...ZERO_SOIL_LAYER, ...payload?.LAYERS?.["30"] },
            "60": { ...ZERO_SOIL_LAYER, ...payload?.LAYERS?.["60"] },
            "100": { ...ZERO_SOIL_LAYER, ...payload?.LAYERS?.["100"] },
            "200": { ...ZERO_SOIL_LAYER, ...payload?.LAYERS?.["200"] },
        },
    };
}

function soilFormToConfiguration(
    values: DSSATPayload,
    config: DSSATSoilConfiguration,
): Omit<DSSATSoilConfiguration, "dssat_soil"> {
    return {
        id: config.id,
        albedo: values.ALBEDO,
        drainage_rate: values.DRAINAGE_RATE,
        runoff_curve_no: values.RUNOFF_CURVE_NO,
        fertility_factor: values.FERTILITY_FACTOR,
        s1_evaporation_limit: values.S1_EVAPORATION_LIMIT,
        mineralization_factor: values.MINERALIZATION_FACTOR,
        source: config.source,
        layers: DSSAT_LAYER_DEPTHS.map((depth) => {
            const existing = config.layers.find(
                (layer) => layer.depth_cm === Number(depth),
            );
            const layer = values.LAYERS[depth];
            const nextLayer: Layer = {
                id: existing?.id ?? 0,
                depth_cm: Number(depth),
                sbdm: layer.SBDM,
                scec: layer.SCEC,
                sdul: layer.SDUL,
                slll: layer.SLLL,
                sloc: layer.SLOC,
                srgf: layer.SRGF,
                ssat: layer.SSAT,
            };
            return nextLayer;
        }),
    };
}

function AdvisorParams({
    onClose,
    fieldId,
}: {
    onClose: () => void;
    fieldId: string;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const [selectedTab, setSelectedTab] = useState("soil");
    const [editingConfigId, setEditingConfigId] = useState<number | null>(null);

    const fieldQuery = useGetRequest<Field>({
        url: `/fields/${fieldId}/`,
        enabled: !!fieldId,
    });
    const advisorParameters = useGetRequest<{
        dssat_soil: DSSATPayload;
        loss_function_parameters: LossFunctionParameters;
    } | null>({
        url: `fields/${fieldId}/advisor_parameters/`,
        enabled: !!fieldId,
    });

    const dssatSoilConfigurations = useGetRequest<DSSATSoilConfiguration[]>({
        url: `fields/${fieldId}/dssat_soil_configurations/`,
        enabled: !!fieldId,
    });

    const advisorParametersMutation = useMutateRequest<{
        dssat_soil: DSSATPayload;
        loss_function_parameters: LossFunctionParameters;
    }>({
        url: `fields/${fieldId}/advisor_parameters/`,
        method: "put",
        successMessage: "Loss function saved",
    });

    const soilConfigurationMutation = useMutateRequest<DSSATSoilConfiguration>({
        method: "put",
        successMessage: "DSSAT soil saved",
        errorMessage: "Failed to save DSSAT soil",
    });

    const populateSoilSources = useMutateRequest({
        url: `fields/${fieldId}/populate_dssat_soil_sources/`,
        method: "post",
        successMessage: "Populating soil sources",
        errorMessage: "Failed to populate soil sources",
    });

    const form = useForm<ParameterFormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            dssat_soil: emptySoilForm(),
            loss_function: {
                w1: 0,
                w2: 0,
                w3: 0,
                w4: 0,
                w5: 0,
                n_cost_per_lb: 0,
                app_cost: 0,
                crop_price: 0,
                bu_per_n: 0,
                niad_thresh: 0,
                remob_thresh: 0,
                roi_safe_frac: 0,
                roi_gap_power: 0,
            },
        },
    });

    const editingConfig = useMemo(
        () =>
            dssatSoilConfigurations.data?.find(
                (config) => config.id === editingConfigId,
            ),
        [dssatSoilConfigurations.data, editingConfigId],
    );

    useEffect(() => {
        if (advisorParameters.data) {
            const { loss_function_parameters } = advisorParameters.data;
            form.reset({
                ...form.getValues(),
                loss_function: loss_function_parameters,
            });
        }
    }, [advisorParameters.data, form]);

    useEffect(() => {
        if (!editingConfig) return;
        form.reset({
            ...form.getValues(),
            dssat_soil: configurationToSoilForm(editingConfig),
        });
    }, [editingConfig, form]);

    const onSubmit = useCallback(
        (values: ParameterFormValues) => {
            if (selectedTab === "soil") {
                if (!editingConfig) return;
                soilConfigurationMutation.mutate(
                    {
                        url: `dssat_soil_configurations/${editingConfig.id}/`,
                        data: soilFormToConfiguration(
                            values.dssat_soil,
                            editingConfig,
                        ),
                    },
                    {
                        onSuccess: () => {
                            dssatSoilConfigurations.refetch();
                            setEditingConfigId(null);
                        },
                    },
                );
                return;
            }

            advisorParametersMutation.mutate(
                {
                    data: {
                        dssat_soil: advisorParameters.data?.dssat_soil,
                        loss_function_parameters: values.loss_function,
                    },
                },
                {
                    onSuccess: () => {
                        advisorParameters.refetch();
                        onClose();
                    },
                },
            );
        },
        [
            selectedTab,
            editingConfig,
            soilConfigurationMutation,
            dssatSoilConfigurations,
            advisorParametersMutation,
            advisorParameters,
            onClose,
        ],
    );

    const [layers] = form.watch(["dssat_soil.LAYERS"]);

    const [soilOrganicMatter, setSoilOrganicMatter] = useState<number | null>(
        null,
    );
    const [initialSoilNitrogen, setInitialSoilNitrogen] = useState<
        number | null
    >(null);
    useEffect(() => {
        if (fieldQuery.data?.soil_organic_matter) {
            setSoilOrganicMatter(fieldQuery.data.soil_organic_matter);
        }
        if (fieldQuery.data?.initial_soil_nitrogen) {
            setInitialSoilNitrogen(fieldQuery.data.initial_soil_nitrogen);
        }
    }, [
        fieldQuery.data?.soil_organic_matter,
        fieldQuery.data?.initial_soil_nitrogen,
    ]);

    const displaySlocByDepth = soilOrganicMatter
        ? displaySlocsAfterFieldOrganicMatter(layers, soilOrganicMatter)
        : null;

    const fieldMutation = useMutateRequest<Field>({
        url: `fields/bulk_update/`,
        method: "patch",
        successMessage: "Soil info saved",
        errorMessage: "Failed to save soil info",
    });

    const saveSoilOrganicMatter = useCallback(() => {
        fieldMutation.mutate({
            data: [
                {
                    id: fieldId,
                    soil_organic_matter: soilOrganicMatter,
                    initial_soil_nitrogen: initialSoilNitrogen,
                },
            ],
        });
    }, [fieldId, fieldMutation, soilOrganicMatter, initialSoilNitrogen]);

    const isSaving =
        advisorParametersMutation.isPending ||
        soilConfigurationMutation.isPending;
    const showSaveButton =
        selectedTab === "loss-function" ||
        (selectedTab === "soil" && editingConfig != null);

    const onSetAsPrimary = useCallback(
        (id: number) => {
            fieldMutation.mutate(
                {
                    url: `fields/${fieldId}/`,
                    data: {
                        active_dssat_soil_configuration: id,
                    },
                },
                {
                    onSuccess: () => {
                        fieldQuery.refetch();
                    },
                },
            );
        },
        [fieldId, fieldMutation, fieldQuery],
    );

    return (
        <Modal isOpen size="3xl" onClose={onClose} isDismissable={false}>
            <ModalContent className="min-h-[80vh]">
                <FormProvider {...form}>
                    <form
                        className="flex flex-col"
                        onSubmit={form.handleSubmit(onSubmit)}
                    >
                        <ModalHeader className="flex flex-row justify-between items-center pr-10">
                            <div className="text-lg font-bold">
                                Advisor Parameters
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <Tabs
                                selectedKey={selectedTab}
                                onSelectionChange={(key) =>
                                    setSelectedTab(String(key))
                                }
                            >
                                <Tab
                                    key="soil"
                                    title="Soil"
                                    className="flex flex-col gap-2"
                                >
                                    <div className="flex flex-row gap-3 items-end">
                                        <Input
                                            type="number"
                                            label="Soil Organic Matter (%)"
                                            size="sm"
                                            step="any"
                                            min={0}
                                            labelPlacement="outside"
                                            placeholder=" "
                                            max={100}
                                            value={
                                                soilOrganicMatter?.toString() ??
                                                undefined
                                            }
                                            onChange={(e) =>
                                                setSoilOrganicMatter(
                                                    isNumber(e.target.value)
                                                        ? Number(e.target.value)
                                                        : null,
                                                )
                                            }
                                        />
                                        <Input
                                            type="number"
                                            label="Initial Soil Nitrogen (lb/ac)"
                                            size="sm"
                                            step="any"
                                            min={0}
                                            labelPlacement="outside"
                                            placeholder=" "
                                            max={150}
                                            value={
                                                initialSoilNitrogen?.toString() ??
                                                undefined
                                            }
                                            onChange={(e) =>
                                                setInitialSoilNitrogen(
                                                    Number(e.target.value),
                                                )
                                            }
                                        />
                                        <div>
                                            <Button
                                                type="button"
                                                color="primary"
                                                size="sm"
                                                onPress={() =>
                                                    saveSoilOrganicMatter()
                                                }
                                                isLoading={
                                                    fieldMutation.isPending
                                                }
                                            >
                                                Save soil properties
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="text-xs text-default-500">
                                        Soil Organic Matter will be used to
                                        scale the SLOC to match this value - see
                                        Adj SLOC
                                    </div>

                                    {editingConfig ? (
                                        <>
                                            <div className="flex flex-row items-center gap-2">
                                                <Button
                                                    type="button"
                                                    variant="light"
                                                    size="sm"
                                                    startContent={
                                                        <FontAwesomeIcon
                                                            icon={faArrowLeft}
                                                        />
                                                    }
                                                    onPress={() =>
                                                        setEditingConfigId(null)
                                                    }
                                                >
                                                    Back
                                                </Button>
                                                <div className="text-sm font-semibold">
                                                    {formatSoilSource(
                                                        editingConfig.source,
                                                    )}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-1 md:grid-cols-3 ">
                                                <NumberInputComponent
                                                    label="ALBEDO"
                                                    name="dssat_soil.ALBEDO"
                                                    size="sm"
                                                    step="any"
                                                    labelPlacement="outside"
                                                />
                                                <NumberInputComponent
                                                    label="DRAINAGE_RATE"
                                                    name="dssat_soil.DRAINAGE_RATE"
                                                    size="sm"
                                                    step="any"
                                                    labelPlacement="outside"
                                                />
                                                <NumberInputComponent
                                                    label="RUNOFF_CURVE_NO"
                                                    name="dssat_soil.RUNOFF_CURVE_NO"
                                                    size="sm"
                                                    step="any"
                                                    labelPlacement="outside"
                                                />
                                                <NumberInputComponent
                                                    label="FERTILITY_FACTOR"
                                                    name="dssat_soil.FERTILITY_FACTOR"
                                                    size="sm"
                                                    step="any"
                                                    labelPlacement="outside"
                                                />
                                                <NumberInputComponent
                                                    label="S1_EVAPORATION_LIMIT"
                                                    name="dssat_soil.S1_EVAPORATION_LIMIT"
                                                    size="sm"
                                                    step="any"
                                                    labelPlacement="outside"
                                                />
                                                <NumberInputComponent
                                                    label="MINERALIZATION_FACTOR"
                                                    name="dssat_soil.MINERALIZATION_FACTOR"
                                                    size="sm"
                                                    step="any"
                                                    labelPlacement="outside"
                                                />
                                            </div>

                                            <Divider />

                                            <div className="overflow-x-auto">
                                                <div
                                                    className="grid min-w-[36rem] gap-x-2 gap-y-2"
                                                    style={{
                                                        gridTemplateColumns:
                                                            "minmax(4.5rem, auto) repeat(8, minmax(0, 1fr))",
                                                    }}
                                                >
                                                    <div className="text-small font-semibold text-default-500">
                                                        Depth (cm)
                                                    </div>
                                                    {DSSAT_LAYER_COLUMNS.map(
                                                        (field) => (
                                                            <div
                                                                key={field}
                                                                className="text-small font-semibold text-center"
                                                            >
                                                                {field}
                                                            </div>
                                                        ),
                                                    )}
                                                    {DSSAT_LAYER_DEPTHS.map(
                                                        (depth) => (
                                                            <div
                                                                key={depth}
                                                                className="contents"
                                                            >
                                                                <div className="text-small font-medium flex items-center">
                                                                    {depth} cm
                                                                </div>
                                                                {DSSAT_LAYER_COLUMNS.map(
                                                                    (field) => {
                                                                        if (
                                                                            field ===
                                                                            "Adj SLOC"
                                                                        ) {
                                                                            const sloc =
                                                                                displaySlocByDepth?.[
                                                                                    depth
                                                                                ];
                                                                            const actualSloc =
                                                                                isNumber(
                                                                                    layers?.[
                                                                                        depth
                                                                                    ]
                                                                                        ?.SLOC,
                                                                                )
                                                                                    ? Number(
                                                                                          layers?.[
                                                                                              depth
                                                                                          ]
                                                                                              ?.SLOC,
                                                                                      )
                                                                                    : null;

                                                                            return (
                                                                                <div
                                                                                    key={`${depth}-${field}`}
                                                                                    className="text-sm flex items-center "
                                                                                >
                                                                                    {sloc
                                                                                        ? sloc.toFixed(
                                                                                              3,
                                                                                          )
                                                                                        : actualSloc?.toFixed(
                                                                                              3,
                                                                                          )}
                                                                                </div>
                                                                            );
                                                                        }
                                                                        return (
                                                                            <NumberInputComponent
                                                                                key={`${depth}-${field}`}
                                                                                name={`dssat_soil.LAYERS.${depth}.${field}`}
                                                                                size="sm"
                                                                            />
                                                                        );
                                                                    },
                                                                )}
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex flex-col gap-2">
                                            <div className="flex justify-end">
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="flat"
                                                    color="primary"
                                                    onPress={() =>
                                                        populateSoilSources.mutate(
                                                            {},
                                                        )
                                                    }
                                                    isLoading={
                                                        populateSoilSources.isPending
                                                    }
                                                >
                                                    Populate soil sources
                                                </Button>
                                            </div>
                                            {dssatSoilConfigurations.isLoading ? (
                                                <div className="flex justify-center py-8">
                                                    <Spinner label="Loading soil configurations..." />
                                                </div>
                                            ) : dssatSoilConfigurations.data
                                                  ?.length ? (
                                                dssatSoilConfigurations.data.map(
                                                    (config) => {
                                                        const isPrimary =
                                                            config.id ===
                                                            fieldQuery.data
                                                                ?.active_dssat_soil_configuration;
                                                        const buttonColor =
                                                            isPrimary
                                                                ? "primary"
                                                                : "default";
                                                        return (
                                                            <Card
                                                                key={config.id}
                                                                isPressable
                                                                onPress={() =>
                                                                    setEditingConfigId(
                                                                        config.id,
                                                                    )
                                                                }
                                                            >
                                                                <CardBody className="flex flex-row items-center justify-between gap-3">
                                                                    <div className="font-medium">
                                                                        {formatSoilSource(
                                                                            config.source,
                                                                        )}
                                                                    </div>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        variant="flat"
                                                                        color={
                                                                            buttonColor
                                                                        }
                                                                        onPress={() =>
                                                                            onSetAsPrimary(
                                                                                config.id,
                                                                            )
                                                                        }
                                                                    >
                                                                        {isPrimary
                                                                            ? "Primary"
                                                                            : "Set as primary"}
                                                                    </Button>
                                                                </CardBody>
                                                            </Card>
                                                        );
                                                    },
                                                )
                                            ) : (
                                                <div className="text-sm text-default-500 py-4 text-center">
                                                    No soil configurations
                                                    found.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Tab>
                                <Tab key="loss-function" title="Loss Function">
                                    <div className="flex flex-col gap-4">
                                        <div className="grid grid-cols-5 gap-3">
                                            <NumberInputComponent
                                                label="w1"
                                                name="loss_function.w1"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="pen_early_cwad_by_rate"
                                            />
                                            <NumberInputComponent
                                                label="w2"
                                                name="loss_function.w2"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="pen_niad_by_rate"
                                            />
                                            <NumberInputComponent
                                                label="w3"
                                                name="loss_function.w3"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="pen_remob_by_rate"
                                            />
                                            <NumberInputComponent
                                                label="w4"
                                                name="loss_function.w4"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="roi_pen_by_rate"
                                            />
                                            <NumberInputComponent
                                                label="w5"
                                                name="loss_function.w5"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="pen_early_nupc_by_rate"
                                            />
                                        </div>
                                        <div className="grid grid-cols-4 gap-3">
                                            <NumberInputComponent
                                                label="n_cost_per_lb"
                                                name="loss_function.n_cost_per_lb"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="app_cost"
                                                name="loss_function.app_cost"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="crop_price"
                                                name="loss_function.crop_price"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="bu_per_n"
                                                name="loss_function.bu_per_n"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="niad_thresh"
                                                name="loss_function.niad_thresh"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="remob_thresh"
                                                name="loss_function.remob_thresh"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="roi_safe_frac"
                                                name="loss_function.roi_safe_frac"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="roi_gap_power"
                                                name="loss_function.roi_gap_power"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                        </div>
                                    </div>
                                </Tab>
                            </Tabs>
                        </ModalBody>
                        <ModalFooter>
                            {showSaveButton ? (
                                <Button
                                    color="primary"
                                    type="submit"
                                    isLoading={isSaving}
                                    isDisabled={isSaving}
                                >
                                    Save
                                </Button>
                            ) : null}
                        </ModalFooter>
                    </form>
                </FormProvider>
            </ModalContent>
        </Modal>
    );
}

export default AdvisorParams;
